import { FC, useCallback, useMemo } from 'react';
import { LineItemDto, QuoteDto } from '../../dtos';
import { Grid, IconButton, TableCell, Tooltip } from '@mui/material';
import { AuthenticatedComponent, DataTableColumn, StandardListViewV2 } from '../../Components/CoreLib/library';
import { triaxReferenceNumberColumnKeySuffixes } from '../../constants/triaxReferenceNumberColumnKeySuffixes';
import { formatCurrency, getBrokerRateFromLineItem, getDriverRateFromLineItem, getHaulingRateFromLineItem, LineItemTypes, QuoteType } from '../../util';
import { useGetLineItemQuoteSummaryLineItemsQuery } from '../../store/generated/generatedApi';
import { AddCircle, Cancel, Warning } from '@mui/icons-material';
import { BilledToType } from '../../dtos/enums/BilledToType';
import { MaterialLineItemDto } from '../../dtos/generated/MaterialLineItemDto';

export interface IQuoteLineItemsOrderViewProps {
    quote: QuoteDto;
    copyQuoteLineItemToOrder?: (lineItem: LineItemDto) => void;
    disableCopy?: boolean;
    maxHeight?: string; // should have a value of 'px' or 'vh'
}

export const QuoteLineItemsOrderView: FC<IQuoteLineItemsOrderViewProps> = ({ quote, copyQuoteLineItemToOrder, disableCopy, maxHeight }) => {

    const renderEditMenuButton = useCallback(
        (row: LineItemDto) => {
            if (!copyQuoteLineItemToOrder || !row.isActive) {
                return null;
            }
            const isFOBandHaulingLineItem = row.type === LineItemTypes.SiteHauling && quote?.type === QuoteType.FOB;

            return (
                <AuthenticatedComponent
                    requiredPermissions={['delete:lineItem']}
                    children={
                        <TableCell padding='none' sx={{ border: 'none' }}>
                            <IconButton
                                size='small'
                                color='primary'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    copyQuoteLineItemToOrder(row);
                                }}
                                disabled={disableCopy || isFOBandHaulingLineItem}>
                                <AddCircle />
                            </IconButton>
                        </TableCell>
                    }
                />
            );
        },
        [copyQuoteLineItemToOrder, disableCopy, quote?.type]
    );

    const tableColumns: DataTableColumn<LineItemDto>[] = useMemo(() => {
        const getMaterialPriceValue = (row: LineItemDto) => {
            if (row.isBilledTo === BilledToType.Customer || row.isBilledTo === BilledToType.Custom) {
                return 'BTC';
            }
            return row.rate !== null ? formatCurrency(row.rate) : '';
        };

        const getStatus = (row: LineItemDto) => {
            let status = 0;
            if (row.type === LineItemTypes.Material) {
                const materialLineItem = row as MaterialLineItemDto;
                if (!row.isActive) {
                    status = 1;
                } else if (materialLineItem.materialPrice?.isActive === false) {
                    status = 3;
                } else if (row.associatedLineItem && !row.associatedLineItem?.isActive) {
                    status = 2;
                }
            } else {
                if (!row.isActive) {
                    status = 1;
                }
            }

            switch (status) {
                case 0:
                    return <></>;
                case 1:
                    return <Tooltip title='Inactive' placement='bottom'><Cancel color='error' /></Tooltip>;
                case 2:
                    return <Tooltip title='This line item is linked to an inactive hauling line item' placement='bottom'><Warning color='warning' /></Tooltip>;
                case 3:
                    return <Tooltip title='This line item uses an inactive material price' placement='bottom'><Warning color='warning' /></Tooltip>;
            }
        };
        let columns = [
            { key: 'description', label: 'Description', sortKey: 'DESCRIPTION', unsorted: true, fieldMapper: (row) => row.description },
            { key: 'status', label: 'Status', sortKey: 'STATUS', align: 'center', unsorted: true, fieldMapper: (row) => getStatus(row) },
            { key: 'site', label: 'Site', sortKey: 'SITE', unsorted: true, fieldMapper: (row) => row.site?.name ?? '' },
            { key: 'type', label: 'Type', sortKey: 'TYPE', unsorted: true },
            { key: 'region', label: 'Region', sortKey: 'REGION', unsorted: true },
            { key: 'equipmentType', label: 'Equip. Type', sortKey: 'EQUIPMENT_TYPE', width: '90px', unsorted: true, fieldMapper: (row) => row.equipmentType?.type },
            { key: 'unitOfMeasure', label: 'Units', sortKey: 'UNIT', align: 'right', unsorted: true },
            {
                key: 'driverRate',
                label: 'Driver Rate',
                sortKey: 'DRIVER_RATE',
                align: 'right',
                unsorted: true,
                fieldMapper: getDriverRateFromLineItem,
            },
            {
                key: 'brokerRate',
                label: 'Broker Rate',
                sortKey: 'BROKER_RATE',
                align: 'right',
                unsorted: true,
                fieldMapper: getBrokerRateFromLineItem,
            },
            {
                key: 'rate',
                label: 'Material Rate',
                sortKey: 'MATERIAL_PRICE_VALUE',
                unsorted: true,
                align: 'right',
                fieldMapper: (row) => getMaterialPriceValue(row),
            },
            {
                key: 'haulingRate',
                label: 'Hauling Rate',
                sortKey: 'HAULING_RATE',
                unsorted: true,
                align: 'right',
                fieldMapper: getHaulingRateFromLineItem,
            },
            {
                key: 'quoteRate',
                label: 'Quote Rate',
                sortKey: 'QUOTE_RATE',
                unsorted: true,
                align: 'right',
                fieldMapper: (row) => (row.quoteRate !== null ? formatCurrency(row.quoteRate) : ''),
            }
        ] as DataTableColumn<LineItemDto>[];

        if (copyQuoteLineItemToOrder) {
            columns.push({
                key: '',
                label: '',
                sortKey: '',
                unsorted: true,
                fieldMapper: (row) => (row.type !== '' ? renderEditMenuButton(row) : <div style={{ padding: '16px' }}></div>),
            });
        }

        return columns;
    }, [renderEditMenuButton, copyQuoteLineItemToOrder]);

    return (
        <Grid container direction='column' spacing={3}>
            <Grid item>
                <StandardListViewV2
                    referenceNumberColumnKeySuffixes={triaxReferenceNumberColumnKeySuffixes}
                    enableMultiSort
                    headerIcon={<></>}
                    headerTitle={''}
                    breadCrumbs={[]}
                    isPaginationVisible={false}
                    getDataQuery={useGetLineItemQuoteSummaryLineItemsQuery}
                    additionalQueryParameters={quote ? { quoteId: quote?.id, isOrderView: true } : undefined}
                    isShowActiveToggleVisible={false}
                    tableColumns={tableColumns}
                    entityNameSingular='Quote'
                    editIcon={<AddCircle />}
                    permissionName='quote'
                    hideSearch
                    disableSelection
                    hideMenu
                    hideAddButtonDivider
                    stickyTableHeader={false}
                    maxTableHeight={maxHeight}
                />
            </Grid>
        </Grid>
    );
};
